import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Empty, DatePicker } from 'antd';
import _ from 'underscore';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { regexInput } from 'utils/regex';
import { EmptyIcon } from 'assets/icon/bigicon';
import Cards from 'design-components/Cards';
import ModalDelete from 'components/ModalDelete';
import { getAccess } from 'utils';
import {
  fetchSchedule,
  deleteSchedule,
  scheduleCleanUp,
} from 'state/actions/schedule';
import { fetchScreensForSite } from 'state/actions/screens';
import './Scheduling.css';

const { RangePicker } = DatePicker;

const Scheduling = ({ history }) => {
  const { siteId, siteName } = useParams();
  const dispatch = useDispatch();

  const {
    list,
    loading,
    updated,
    screens,
    userData,
  } = useSelector(
    (state) => ({
      list: state.schedule.list,
      loading: state.schedule.loading,
      updated: state.schedule.updated,
      screens: state.screens.list,
      userData: state.auth.userData,
    }),
    shallowEqual
  );

  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('published');
  const [dateFilter, setDateFilter] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [access, setAccess] = useState(null);

  const didInit = useRef(false);

  useEffect(() => {
    if (!siteId) return;
    dispatch(fetchSchedule({ siteId }));
    dispatch(fetchScreensForSite({ siteId }));
    didInit.current = true;
  }, [siteId, dispatch]);

  useEffect(() => {
    if (didInit.current && updated && !loading) {
      dispatch(scheduleCleanUp());
      dispatch(fetchSchedule({ siteId }));
    }
  }, [updated, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userData && userData.roleAccess) {
      setAccess(getAccess(userData, 'tenant', siteId));
    }
  }, [userData, siteId]);

  const screenNameById = useMemo(() => {
    const map = {};
    (screens || []).forEach((s) => {
      map[s._id || s.id] = s.name;
    });
    return map;
  }, [screens]);

  const handleSearch = (e) => {
    setError(null);
    if (!regexInput(e.target.value) && e.target.value) {
      setError('Search cannot using special character');
    }
    setSearch(e.target.value);
  };

  const handleEdit = (item) => {
    history.push(`/${siteId}/information/${siteName}/schedule/${item._id}`);
  };

  const handleDelete = () => {
    if (!deleteModal.item) return;
    dispatch(
      deleteSchedule({ id: deleteModal.item._id, siteId }, () =>
        setDeleteModal({ open: false, item: null })
      )
    );
  };

  const data = useMemo(() => {
    let arr = (list || []).filter(Boolean);

    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((it) => (it.name || '').toLowerCase().includes(q));
    }

    if (selectedTab === 'published') {
      arr = arr.filter((it) => !it.status || it.status === 'published');
    } else if (selectedTab === 'unpublished') {
      arr = arr.filter((it) => it.status === 'unpublished');
    } else if (selectedTab === 'archived') {
      arr = arr.filter((it) => it.status === 'archived');
    }

    if (dateFilter && dateFilter[0] && dateFilter[1]) {
      const from = dateFilter[0].valueOf();
      const to = dateFilter[1].valueOf();
      arr = arr.filter((it) => {
        if (!it.startDate || !it.endDate) return false;
        const s = new Date(it.startDate).valueOf();
        const e = new Date(it.endDate).valueOf();
        return e >= from && s <= to;
      });
    }

    return arr.map((it) => {
      const ids = (it.screenIds || []).map((s) =>
        typeof s === 'string' ? s : s?._id || s?.id
      );
      const assignedScreens = ids
        .map((id) => ({ name: screenNameById[id] }))
        .filter((s) => s.name);
      const modifiedBy =
        it.modifiedBy && typeof it.modifiedBy === 'object'
          ? it.modifiedBy
          : { email: it.modifiedBy || it.createdBy || '-' };
      return { ...it, assignedScreens, modifiedBy };
    });
  }, [list, search, selectedTab, dateFilter, screenNameById]);

  return (
    <div className="media-library">
      <ModalDelete
        isVisible={deleteModal.open}
        onOk={handleDelete}
        onCancel={() => setDeleteModal({ open: false, item: null })}
        title="Delete Schedule"
        subtitle={deleteModal.item ? deleteModal.item.name : ''}
        loading={loading}
      />

      <div className="media-header">
        <div className="media-header-left">
          <div className="searchbar-wrapper">
            <div className="searchbar">
              <i className="feather-search" />
              <input
                placeholder="Search"
                className="search-input"
                value={search}
                onChange={handleSearch}
              />
            </div>
            {error && <div className="search-error">{error}</div>}
          </div>

          <div className="scheduling-date-filter">
            <RangePicker
              value={dateFilter}
              onChange={setDateFilter}
              placeholder={['Start Date', 'End Date']}
            />
          </div>
        </div>
        <div className="actions__wrapper">
            <Link
              to={`/${siteId}/information/${siteName}/schedule/new`}
              className="btn-primary"
            >
              <i className="feather-plus mr-1" />
              Add Schedule
            </Link>
        </div>
      </div>

      <div className="media-content-wrapper">
        <div className="media-content">
          <div className="media-tab-wrapper">
            <div className="media-tab-left">
              <div
                onClick={() => setSelectedTab('published')}
                className={`media-tab ${
                  selectedTab === 'published' ? 'active' : ''
                }`}
              >
                Published Items
              </div>
              <div
                onClick={() => setSelectedTab('unpublished')}
                className={`media-tab ${
                  selectedTab === 'unpublished' ? 'active' : ''
                }`}
              >
                Unpublished Items
              </div>
              <div
                onClick={() => setSelectedTab('archived')}
                className={`media-tab ${
                  selectedTab === 'archived' ? 'active' : ''
                }`}
              >
                Archived Items
              </div>
            </div>
          </div>

          <div className="media-list-content">
            {data.length > 0 ? (
              _.chunk(data, 3).map((chunk, index) => (
                <div className="media-list-chunk" key={index}>
                  {chunk.map((item) => (
                    <div
                      key={item._id}
                      data-id={item._id}
                      className="media-list"
                    >
                      <Cards
                        data={item}
                        type="scheduling"
                        isShowMedia={false}
                        editAction={() => handleEdit(item)}
                        deleteAction={() =>
                          setDeleteModal({ open: true, item })
                        }
                      />
                    </div>
                  ))}
                  {chunk.length < 2 && <div className="media-list-empty" />}
                  {chunk.length < 3 && <div className="media-list-empty" />}
                </div>
              ))
            ) : (
              <div className="media-empty">
                <Empty image={EmptyIcon} description="No Schedule" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scheduling;